import {
    Box,
    HStack,
    VStack,
    Heading,
    Icon,
    Text as ChakraText,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { useEffect, useState, useMemo } from 'react';
// (2993) Reverted — commented out rather than deleted, see commit cfe2c9c58.
// import { useRef } from 'react';
import { MdDragIndicator } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import {
    isBranchRoutingEnabled,
    isFailoverRoutingEnabled,
} from '@/constants/featureFlags';

import {
    getPlatformConfig,
    getItemStyle,
    getListStyle,
    getCloneItemStyle,
    getGroupStyle,
} from '../utils/getDragAndDropHelpers';

import {
    createGroup,
    calculateFinalPrioritiesFromUnified,
    autoGroupByPriority,
} from './utils/groupHelpers';

import FormAccordionBase from '@/components/forms/new/FormAccordionBase';
import {
    accordionHeadingStyling,
    headingStyling,
} from '@/components/forms/new/formStyles';
import { Button, LoadingIndicator } from '@/components/v4';
import Text from '@/components/v4/Text';
import { classNames } from '@/utils/classNames';
import TrunkType from '@/utils/enums/TrunkType';
import { PlatformEnums } from '@/utils/platforms';
import CountryFlag from 'components/icons/CountryFlag';
import Drawer from 'components/presentational/Drawer';
import Services from 'pages/accounts/users/services/Services';
import { ServicesProvider } from 'pages/accounts/users/services/ServicesContext';
import useGetTRSRegistration from 'pages/resources/aors/queries/useGetTRSRegistration';
import useGetAddresses from '@/queries/company/useGetAddresses';
import useGetAssignedPlans from '../queries/useGetAssignedPlans';
import { getNumberSaveScope } from './utils/numberSaveScope';
import useGetNumberById from '../queries/useGetNumberById';
import useGetTeamsTrunks from '../queries/useGetTeamsTrunks';
import useUpdateAllPlatformNumbers from '../queries/useUpdateAllPlatformNumbers';
import useUpdateNumber from '../queries/useUpdateNumber';
// (2993) import useGetNumberActivatedServices from '../queries/useGetNumberActivatedServices';
import EditNumberAccordion from './accordionComponents/EditNumberAccordion';
import PlatformAccordion from './accordionComponents/PlatformAccordion';
import GroupAccordion from './accordionComponents/GroupAccordion';
import StatusCellRenderer from './cellRenderer/StatusCellRenderer';
import PlatformNumberWarning from './PlatformNumberWarning';
import GroupDropZone from './GroupDropZone';

import useGetCurrentCompany from '@/queries/company/useGetCurrentCompany';
import useGetCurrentCompanyVendorService from '@/queries/company/useGetCurrentCompanyVendorService';
const EditPlatformNumberDrawer = ({
    isOpen,
    onClose,
    expandedPlatformNumbers,
    isFetchingPlatformNumbers,
    isLoadingPlatformNumbers,
    selectedPlatform,
    platformRefetch,
    refreshTable,
    currentNumber,
    outboundCallerIDs,
    isFetchingOutboundCallerIDs,
    platformList,
    OC,
    trsRegistrations,
    tableRowData,
}) => {
    const { currentScope, requiredScope } = useSelector((state) => {
        return { ...state.login };
    });

    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });
    const { data: companyData } = useGetCurrentCompany();
    const { data: vendorService } = useGetCurrentCompanyVendorService();
    // (2993) Reverted — commented out rather than deleted, see commit cfe2c9c58.
    // const { data: activatedServices } =
    //     useGetNumberActivatedServices(currentNumber);
    // const hasActivatedServices = (activatedServices?.length ?? 0) > 0;
    // const servicesRef = useRef(null);

    const selectedPlatformIndex = Array.isArray(expandedPlatformNumbers)
        ? (() => {
              const index = expandedPlatformNumbers.findIndex(
                  (platform) =>
                      platform?.platformID === selectedPlatform?.platformID,
              );
              return index === -1 ? 0 : index + 1;
          })()
        : 0;

    const [openIndexes, setOpenIndexes] = useState([selectedPlatformIndex]);
    const [teamsTrunks, setTeamsTrunks] = useState([]);
    const [callingProfiles, setCallingProfiles] = useState([]);
    const [selectedTrunk, setSelectedTrunk] = useState();

    useEffect(() => {
        setOpenIndexes([selectedPlatformIndex]);
    }, [selectedPlatformIndex, isOpen]);

    // Clear pending group platforms when drawer closes to prevent state persistence
    useEffect(() => {
        if (!isOpen) {
            setPendingGroupPlatforms([]);
            setIsDragging(false);
        }
    }, [isOpen]);

    // Only manipulates grouping (FRONTEND STATE ONLY, dooes not exist in backend data) and priority ONLY
    const [platformItems, setPlatformItems] = useState([]);
    const [initialPlatformItems, setInitialPlatformItems] = useState([]);
    // Maintains initial data, based on expandedPlatformNumbers
    const [previousData, setPreviousData] = useState([]);
    // Manipulates form data, based on expandedPlatformNumbers
    const [submitData, setSubmitData] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [isDraggingGroup, setIsDraggingGroup] = useState(false);
    const [pendingGroupPlatforms, setPendingGroupPlatforms] = useState([]);

    useEffect(() => {
        // Don't update submitData if there are pending platforms in the drop zone
        if (pendingGroupPlatforms.length > 0) {
            return;
        }

        // const standalones = platformItems.filter(
        //     (item) => item.type !== 'group',
        // );
        // setSubmitData(standalones);
    }, [platformItems, pendingGroupPlatforms]);

    const [editNumberData, setEditNumberData] = useState([]);
    const [previousEditNumberData, setPreviousEditNumberData] = useState([]);

    // Compute feature flags from editNumberData
    const simRingEnabled = useMemo(() => {
        return isBranchRoutingEnabled(editNumberData?.enabledFeatureIDs);
    }, [editNumberData?.enabledFeatureIDs]);

    const failoverEnabled = useMemo(() => {
        return isFailoverRoutingEnabled(editNumberData?.enabledFeatureIDs);
    }, [editNumberData?.enabledFeatureIDs]);

    useGetTeamsTrunks(
        currentCompany,
        currentScope,
        setTeamsTrunks,
        PlatformEnums.TEAMS,
        setCallingProfiles,
        isOpen,
        platformList,
    );

    const { data: registrationData, isLoading: isLoadingRegistration } =
        useGetTRSRegistration(previousEditNumberData?.registrationID);

    // Initial set
    useEffect(() => {
        if (!expandedPlatformNumbers || expandedPlatformNumbers.length === 0) {
            setPlatformItems([]);
            setInitialPlatformItems([]);
            setPreviousData([]);
            setSubmitData([]);
            return;
        }

        const platformsWithType = (expandedPlatformNumbers || []).map(
            (platform) => ({
                ...platform,
                type: 'platform',
            }),
        );

        const finalItems = autoGroupByPriority(
            platformsWithType,
            simRingEnabled,
        );

        setPlatformItems(finalItems);
        setInitialPlatformItems(finalItems);
        setPreviousData(expandedPlatformNumbers);
        setSubmitData(expandedPlatformNumbers);
    }, [expandedPlatformNumbers, currentNumber, simRingEnabled]);

    const { mutate: updateAllPlatformNumbers } = useUpdateAllPlatformNumbers(
        submitData,
        currentScope,
        platformRefetch,
        refreshTable,
        previousData,
        onClose,
    );
    const { isFetching, refetch } = useGetNumberById(
        currentNumber,
        setEditNumberData,
        setPreviousEditNumberData,
    );

    const { mutate: editNumber } = useUpdateNumber({
        data: editNumberData,
        accountScope: getNumberSaveScope({
            perNumberPlan: companyData?.companyBillingSettings?.perNumberPlan,
            accountScope: requiredScope,
            viewScope: currentScope,
        }),
        refetchNumber: refetch,
        refreshTable,
        previousEditNumberData,
        onClose,
    });

    const [addresses, setAddresses] = useState([]);

    const { isFetching: isFetchingAddresses } = useGetAddresses(
        currentCompany,
        setAddresses,
    );

    const [assignedPlans, setAssignedPlans] = useState([]);

    const { isFetching: isFetchingAssignedPlans } = useGetAssignedPlans(
        currentCompany,
        setAssignedPlans,
    );

    const finalFormSubmit = (value) => {
        const flattenedPlatforms =
            calculateFinalPrioritiesFromUnified(platformItems);
        setSubmitData((prevSubmitData) =>
            prevSubmitData.map((platform) => {
                const updatedPlatform = flattenedPlatforms.find(
                    (p) => p.platformID === platform.platformID,
                );
                if (updatedPlatform) {
                    return { ...platform, priority: updatedPlatform.priority };
                }
                return platform;
            }),
        );
        setEditNumberData(() => ({
            ...value?.formValues[0],
        }));
        setTimeout(() => {
            editNumber();
            updateAllPlatformNumbers();
            // (2993) servicesRef.current?.submitAll?.();
        }, 0);
    };

    const isFetchingData =
        isFetching ||
        isFetchingAddresses ||
        isFetchingOutboundCallerIDs ||
        isFetchingPlatformNumbers ||
        isFetchingAssignedPlans ||
        isLoadingPlatformNumbers ||
        (previousEditNumberData?.registrationID && isLoadingRegistration);

    const extraCards = [
        <Box key="servicesHeader" heading="Add On Services">
            <ServicesProvider key="numbsersServicesProvider">
                {/* (2993) Reverted — commented out rather than deleted, see commit cfe2c9c58.
                <Services
                    ref={servicesRef}
                    numberID={currentNumber}
                    hideIndividualSaveButton
                /> */}
                <Services numberID={currentNumber} />
            </ServicesProvider>
        </Box>,
    ];

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const handleDragStart = (start) => {
        setIsDragging(true);

        // Detect if we're dragging a group vs a platform
        const draggedItem = platformItems.find((item) => {
            if (item.type === 'group') {
                return item.id === start.draggableId;
            } else {
                return item.platformID === start.draggableId;
            }
        });

        setIsDraggingGroup(draggedItem?.type === 'group');
    };

    const handleDragEnd = (result) => {
        const { source, destination, draggableId } = result;

        requestAnimationFrame(() => {
            setIsDragging(false);
            setIsDraggingGroup(false);
        });

        if (!destination) {
            setPendingGroupPlatforms([]);
            return;
        }

        const sourceDroppableId = source.droppableId;
        const destinationDroppableId = destination.droppableId;
        const extractPlatformFromUnified = (items, platformId) => {
            let removedPlatform = null;
            const updatedItems = [];

            items.forEach((item) => {
                if (item.type === 'group') {
                    const platform = item.platforms.find(
                        (p) => p.platformID === platformId,
                    );
                    if (platform) {
                        removedPlatform = platform;
                        const remainingPlatforms = item.platforms.filter(
                            (p) => p.platformID !== platformId,
                        );
                        if (remainingPlatforms.length >= 2) {
                            updatedItems.push({
                                ...item,
                                platforms: remainingPlatforms,
                            });
                        } else {
                            // Group dissolved - add remaining platforms back as standalone
                            remainingPlatforms.forEach((p) => {
                                updatedItems.push({ ...p, type: 'platform' });
                            });
                        }
                    } else {
                        updatedItems.push(item);
                    }
                } else if (item.platformID === platformId) {
                    removedPlatform = item;
                } else {
                    updatedItems.push(item);
                }
            });

            return { updatedItems, removedPlatform };
        };

        if (destinationDroppableId === 'new-group-zone') {
            const platformId = draggableId.includes('group-')
                ? draggableId.split('-').slice(2).join('-')
                : draggableId;

            // Find the platform but don't remove it yet
            let platformToAdd = null;
            platformItems.forEach((item) => {
                if (item.type === 'group') {
                    const platform = item.platforms.find(
                        (p) => p.platformID === platformId,
                    );
                    if (platform) platformToAdd = platform;
                } else if (item.platformID === platformId) {
                    platformToAdd = item;
                }
            });

            if (platformToAdd) {
                const standalonePlatforms = platformItems.filter(
                    (item) =>
                        item.type !== 'group' &&
                        !pendingGroupPlatforms.some(
                            (p) => p.platformID === item.platformID,
                        ),
                );

                const standaloneCount = standalonePlatforms.length;

                // This prevents leaving a single standalone platform alone
                let platformsForGroup = [
                    ...pendingGroupPlatforms,
                    platformToAdd,
                ];

                // Only auto-include when starting a NEW group with exactly 2 total standalones
                // This prevents orphaning a single platform when only 2 exist
                if (
                    standaloneCount === 2 &&
                    pendingGroupPlatforms.length === 0
                ) {
                    const lastStandalone = standalonePlatforms.find(
                        (p) => p.platformID !== platformId,
                    );
                    platformsForGroup.push(lastStandalone);
                }

                const newPending = platformsForGroup;
                setPendingGroupPlatforms(newPending);

                if (newPending.length >= 2) {
                    // Remove all pending platforms from the main list
                    let finalItems = platformItems;
                    newPending.forEach((platform) => {
                        const { updatedItems } = extractPlatformFromUnified(
                            finalItems,
                            platform.platformID,
                        );
                        finalItems = updatedItems;
                    });

                    // Create the new group with all platforms
                    const newGroup = createGroup(newPending, 10);
                    setPlatformItems([newGroup, ...finalItems]);
                    setPendingGroupPlatforms([]);
                }
            }
            return;
        }

        if (destinationDroppableId.startsWith('group-')) {
            const targetGroupId = destinationDroppableId.replace('group-', '');
            const platformId = draggableId.includes('group-')
                ? draggableId.split('-').slice(2).join('-')
                : draggableId;

            const { updatedItems, removedPlatform } =
                extractPlatformFromUnified(platformItems, platformId);

            if (removedPlatform) {
                const finalItems = updatedItems.map((item) => {
                    if (item.type === 'group' && item.id === targetGroupId) {
                        return {
                            ...item,
                            platforms: [...item.platforms, removedPlatform],
                        };
                    }
                    return item;
                });

                setPlatformItems(finalItems);
            }
            return;
        }

        // Handle reordering within groups
        if (
            sourceDroppableId.startsWith('group-') &&
            destinationDroppableId.startsWith('group-') &&
            sourceDroppableId === destinationDroppableId
        ) {
            const groupId = sourceDroppableId.replace('group-', '');
            const updatedItems = platformItems.map((item) => {
                if (item.type === 'group' && item.id === groupId) {
                    const reorderedPlatforms = reorder(
                        item.platforms,
                        source.index,
                        destination.index,
                    );
                    return { ...item, platforms: reorderedPlatforms };
                }
                return item;
            });
            setPlatformItems(updatedItems);
            return;
        }

        // Handle reordering in unified list (groups and platforms together)
        if (
            sourceDroppableId === 'unified-list' &&
            destinationDroppableId === 'unified-list'
        ) {
            const sourceIndex = source.index;
            const destinationIndex = destination.index;

            // If dropped in the same position, do nothing
            if (sourceIndex === destinationIndex) {
                return;
            }

            const sourceItem = platformItems[sourceIndex];
            const targetItem = platformItems[destinationIndex];

            if (
                sourceItem &&
                sourceItem.type !== 'group' &&
                targetItem &&
                targetItem.type === 'group'
            ) {
                const { updatedItems, removedPlatform } =
                    extractPlatformFromUnified(
                        platformItems,
                        sourceItem.platformID,
                    );

                if (removedPlatform) {
                    const finalItems = updatedItems.map((item) => {
                        if (
                            item.type === 'group' &&
                            item.id === targetItem.id
                        ) {
                            return {
                                ...item,
                                platforms: [...item.platforms, removedPlatform],
                            };
                        }
                        return item;
                    });
                    setPlatformItems(finalItems);
                    return;
                }
            }

            // Normal reorder logic
            const reorderedItems = reorder(
                platformItems,
                sourceIndex,
                destinationIndex,
            );

            // Update priority values based on new order
            reorderedItems.forEach((item, index) => {
                item.priority = (index + 1) * 10;
            });

            setPlatformItems(reorderedItems);
            return;
        }

        // Handle moving from group back to unified list
        if (
            sourceDroppableId.startsWith('group-') &&
            destinationDroppableId === 'unified-list'
        ) {
            // Extract platform ID from draggableId
            const platformId = draggableId.includes('group-')
                ? draggableId.split('-').slice(2).join('-')
                : draggableId;

            const { updatedItems, removedPlatform } =
                extractPlatformFromUnified(platformItems, platformId);

            if (removedPlatform) {
                // Insert at destination index as standalone platform
                const newItems = [...updatedItems];
                newItems.splice(destination.index, 0, {
                    ...removedPlatform,
                    type: 'platform',
                });
                setPlatformItems(newItems);
            }
        }
    };

    const handleCloseGroup = (groupId) => {
        // Find the group and convert its platforms back to standalone
        const updatedItems = [];
        platformItems.forEach((item) => {
            if (item.type === 'group' && item.id === groupId) {
                // Convert group platforms back to standalone
                item.platforms.forEach((platform) => {
                    updatedItems.push({ ...platform, type: 'platform' });
                });
            } else {
                updatedItems.push(item);
            }
        });
        setPlatformItems(updatedItems);
    };

    const handleRemoveFromPending = (platformId) => {
        setPendingGroupPlatforms((prev) =>
            prev.filter((p) => p.platformID !== platformId),
        );
    };

    // Move item up in the list (decrease index = increase priority)
    const handleMoveUp = (itemId, isGroup = false) => {
        const visibleItems = platformItems.filter((item) => {
            if (item.type === 'group') return true;
            return !pendingGroupPlatforms.some(
                (p) => p.platformID === item.platformID,
            );
        });

        const currentIndex = visibleItems.findIndex((item) =>
            isGroup ? item.id === itemId : item.platformID === itemId,
        );

        if (currentIndex <= 0) return; // Already at top

        // Find the actual index in platformItems
        const actualCurrentIndex = platformItems.findIndex((item) =>
            isGroup ? item.id === itemId : item.platformID === itemId,
        );

        // Find the previous visible item and its actual index
        const prevVisibleItem = visibleItems[currentIndex - 1];
        const actualPrevIndex = platformItems.findIndex((item) =>
            prevVisibleItem.type === 'group'
                ? item.id === prevVisibleItem.id
                : item.platformID === prevVisibleItem.platformID,
        );

        const reorderedItems = reorder(
            platformItems,
            actualCurrentIndex,
            actualPrevIndex,
        );
        setPlatformItems(reorderedItems);
    };

    // Move item down in the list (increase index = decrease priority)
    const handleMoveDown = (itemId, isGroup = false) => {
        const visibleItems = platformItems.filter((item) => {
            if (item.type === 'group') return true;
            return !pendingGroupPlatforms.some(
                (p) => p.platformID === item.platformID,
            );
        });

        const currentIndex = visibleItems.findIndex((item) =>
            isGroup ? item.id === itemId : item.platformID === itemId,
        );

        if (currentIndex >= visibleItems.length - 1) return; // Already at bottom

        // Find the actual index in platformItems
        const actualCurrentIndex = platformItems.findIndex((item) =>
            isGroup ? item.id === itemId : item.platformID === itemId,
        );

        // Find the next visible item and its actual index
        const nextVisibleItem = visibleItems[currentIndex + 1];
        const actualNextIndex = platformItems.findIndex((item) =>
            nextVisibleItem.type === 'group'
                ? item.id === nextVisibleItem.id
                : item.platformID === nextVisibleItem.platformID,
        );

        const reorderedItems = reorder(
            platformItems,
            actualCurrentIndex,
            actualNextIndex,
        );
        setPlatformItems(reorderedItems);
    };

    const handleRemovePlatformFromGroup = (groupId, platformId) => {
        // Helper to extract platform from unified list (defined inline for closure)
        const extractPlatformFromUnified = (items, platformId) => {
            let removedPlatform = null;
            const updatedItems = [];

            items.forEach((item) => {
                if (item.type === 'group') {
                    const platform = item.platforms.find(
                        (p) => p.platformID === platformId,
                    );
                    if (platform) {
                        removedPlatform = platform;
                        const remainingPlatforms = item.platforms.filter(
                            (p) => p.platformID !== platformId,
                        );
                        if (remainingPlatforms.length >= 2) {
                            updatedItems.push({
                                ...item,
                                platforms: remainingPlatforms,
                            });
                        } else {
                            // Group dissolved - add remaining platforms back as standalone
                            remainingPlatforms.forEach((p) => {
                                updatedItems.push({ ...p, type: 'platform' });
                            });
                        }
                    } else {
                        updatedItems.push(item);
                    }
                } else if (item.platformID === platformId) {
                    removedPlatform = item;
                } else {
                    updatedItems.push(item);
                }
            });

            return { updatedItems, removedPlatform };
        };

        const { updatedItems, removedPlatform } = extractPlatformFromUnified(
            platformItems,
            platformId,
        );

        if (removedPlatform) {
            // Add the removed platform back as standalone at the end of the list
            setPlatformItems([
                ...updatedItems,
                { ...removedPlatform, type: 'platform' },
            ]);
        }
    };

    const { darkMode } = useSelector((state) => state.settings);

    const renderClone = (provided, snapshot, rubric) => {
        const draggableId = rubric.draggableId;
        const groupItem = platformItems.find(
            (item) => item.type === 'group' && item.id === draggableId,
        );
        const orderNumber =
            platformItems.findIndex((item) => item.id === draggableId) + 1;

        if (groupItem) {
            const cloneStyle = getCloneItemStyle(
                snapshot.isDragging,
                provided.draggableProps.style,
                snapshot.isDropAnimating,
            );

            return (
                <div
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    ref={provided.innerRef}
                    style={cloneStyle}>
                    <Box
                        bg={darkMode ? 'dark.bgDark' : 'white'}
                        border="2px solid"
                        borderColor={
                            snapshot.isDropAnimating ? 'gray.400' : 'blue.300'
                        }
                        borderRadius="lg"
                        shadow={snapshot.isDropAnimating ? 'lg' : 'xl'}
                        opacity={
                            snapshot.isDropAnimating
                                ? 0.7
                                : snapshot.isDragging
                                  ? 0.95
                                  : 1
                        }
                        position="relative"
                        overflow="hidden">
                        <HStack spacing={0} position="relative" p={4}>
                            {platformItems.length > 1 &&
                                !snapshot.isDropAnimating && (
                                    <Box
                                        mr={2}
                                        cursor={
                                            snapshot.isDragging
                                                ? 'grabbing'
                                                : 'grab'
                                        }
                                        display="flex"
                                        alignItems="center"
                                        px={1}>
                                        <Icon
                                            as={MdDragIndicator}
                                            color={
                                                darkMode ? 'white' : 'gray.500'
                                            }
                                            fontSize="20px"
                                        />
                                    </Box>
                                )}
                            <Box flex="1" display="flex" alignItems="center">
                                <HStack flex="1" spacing={3}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        w={8}
                                        h={8}
                                        fontWeight="semibold"
                                        fontSize="sm"
                                        color="messenger.500">
                                        {orderNumber}
                                    </Box>
                                    <Heading
                                        sx={accordionHeadingStyling}
                                        className={classNames(
                                            darkMode
                                                ? 'text-white'
                                                : 'text-black',
                                        )}>
                                        Branch Routing Group (
                                        {groupItem.platforms.length} platforms)
                                    </Heading>
                                </HStack>
                            </Box>
                        </HStack>

                        {!snapshot.isDropAnimating && (
                            <Box
                                px={4}
                                pb={4}
                                pt={0}
                                borderTop="1px solid"
                                borderColor={
                                    darkMode ? 'whiteAlpha.200' : 'gray.200'
                                }>
                                <VStack spacing={2} align="stretch" mt={3}>
                                    {groupItem.platforms.map(
                                        (platform, index) => {
                                            return (
                                                <Box
                                                    key={platform.platformID}
                                                    p={2}
                                                    bg={
                                                        darkMode
                                                            ? 'whiteAlpha.100'
                                                            : 'gray.50'
                                                    }
                                                    borderRadius="md">
                                                    <PlatformAccordion
                                                        platformId={
                                                            platform.platformID
                                                        }
                                                        platform={platform}
                                                        errors={rubric.errors}
                                                        submitData={submitData}
                                                        setSubmitData={
                                                            setSubmitData
                                                        }
                                                        teamsTrunks={
                                                            teamsTrunks
                                                        }
                                                        callingProfiles={
                                                            callingProfiles
                                                        }
                                                        OC={OC}
                                                        platformIndex={index}
                                                        platformNumbersLength={
                                                            groupItem.platforms
                                                                .length
                                                        }
                                                        selectedTrunk={
                                                            selectedTrunk
                                                        }
                                                        setSelectedTrunk={
                                                            setSelectedTrunk
                                                        }
                                                        previousData={
                                                            previousData
                                                        }
                                                    />
                                                </Box>
                                            );
                                        },
                                    )}
                                </VStack>
                            </Box>
                        )}
                    </Box>
                </div>
            );
        }

        const platformId = draggableId.includes('group-')
            ? draggableId.split('-').slice(2).join('-')
            : draggableId;

        // Find platform in unified platformItems list
        let platform;
        if (draggableId.includes('group-')) {
            // Platform is in a group - search all groups in unified list
            const groupId = draggableId.split('-')[1];
            const group = platformItems.find(
                (item) => item.type === 'group' && item.id === groupId,
            );
            platform = group?.platforms.find(
                (p) => p.platformID === platformId,
            );
        } else {
            // Platform is standalone - find in unified list by ID, not index
            platform = platformItems.find(
                (item) =>
                    item.type !== 'group' && item.platformID === platformId,
            );
        }

        if (!platform) return null;

        const platformConfig = getPlatformConfig(platform.platformID);
        const previousPlatform =
            previousData?.find(
                (item) => item.platformID === platform.platformID,
            ) || {};

        const status =
            previousPlatform?.platformStatus === 0 &&
            previousPlatform?.trunkID == null
                ? 'disabled'
                : previousPlatform?.platformStatus;

        const accordionAdornment = (
            <HStack paddingRight={10}>
                {platform?.user && (
                    <Text minWidth="200px" fontWeight="bold">
                        {platform?.user}
                    </Text>
                )}
                <Box width="110px">
                    <StatusCellRenderer value={status} />
                </Box>
            </HStack>
        );

        // Calculate proper styling based on drag state
        const cloneStyle = getCloneItemStyle(
            snapshot.isDragging,
            provided.draggableProps.style,
            snapshot.isDropAnimating,
        );

        return (
            <div
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                ref={provided.innerRef}
                style={cloneStyle}>
                {/* Pill-style drag preview */}
                <Box
                    h="41px"
                    px={2.5}
                    display="flex"
                    alignItems="center"
                    gap={3}
                    bg={darkMode ? 'dark.bgDark' : 'white'}
                    border="2px solid"
                    borderColor={
                        snapshot.isDropAnimating ? 'gray.400' : 'brand.400'
                    }
                    borderRadius="lg"
                    shadow={snapshot.isDropAnimating ? 'lg' : 'xl'}
                    opacity={
                        snapshot.isDropAnimating
                            ? 0.7
                            : snapshot.isDragging
                              ? 0.95
                              : 1
                    }
                    position="relative">
                    {/* Drag handle */}
                    {platformItems.length > 1 && !snapshot.isDropAnimating && (
                        <Icon
                            as={MdDragIndicator}
                            fontSize="20px"
                            color={darkMode ? 'gray.400' : 'gray.500'}
                        />
                    )}

                    {/* Number indicator */}
                    {platformItems.length > 1 && (
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            minW={8}
                            h={8}
                            fontWeight="semibold"
                            fontSize="sm"
                            color="messenger.500">
                            {(() => {
                                // Enhance function to calculate visible index excluding pending platforms
                                const visibleItems = platformItems.filter(
                                    (item) => {
                                        if (item.type === 'group') return true;
                                        return !pendingGroupPlatforms.some(
                                            (p) =>
                                                p.platformID ===
                                                item.platformID,
                                        );
                                    },
                                );

                                return (
                                    visibleItems.findIndex(
                                        (item) =>
                                            item.platformID ===
                                                platform.platformID ||
                                            (item.type === 'group' &&
                                                item.platforms?.some(
                                                    (p) =>
                                                        p.platformID ===
                                                        platform.platformID,
                                                )),
                                    ) + 1
                                );
                            })()}
                        </Box>
                    )}

                    {/* Platform Icon & Name */}
                    <HStack spacing={3} flex="1" minW={0}>
                        {platformConfig.icon && (
                            <Icon as={platformConfig.icon} fontSize="25px" />
                        )}
                        <ChakraText
                            fontWeight="semibold"
                            fontSize="md"
                            className={classNames(
                                darkMode ? 'text-white' : 'text-black',
                            )}
                            noOfLines={1}
                            flex="1">
                            {platformConfig.name}
                        </ChakraText>
                    </HStack>

                    {/* Adornment */}
                    {accordionAdornment && !snapshot.isDropAnimating && (
                        <Box>{accordionAdornment}</Box>
                    )}

                    {/* Expand icon placeholder */}
                    <Icon
                        as={ChevronDownIcon}
                        fontSize="20px"
                        color={darkMode ? 'gray.400' : 'gray.600'}
                    />
                </Box>
            </div>
        );
    };

    const accordionFields = (errors, touched, values) => {
        if (!Array.isArray(platformItems)) {
            return [];
        }

        const dropZoneContent = (
            <Droppable droppableId="new-group-zone" type="PLATFORM">
                {(provided, snapshot) => (
                    <Box ref={provided.innerRef} {...provided.droppableProps}>
                        <GroupDropZone
                            pendingPlatforms={pendingGroupPlatforms}
                            isDraggingOver={snapshot.isDraggingOver}
                            previousData={previousData}
                            submitData={submitData}
                            setSubmitData={setSubmitData}
                            teamsTrunks={teamsTrunks}
                            callingProfiles={callingProfiles}
                            OC={OC}
                            selectedTrunk={selectedTrunk}
                            setSelectedTrunk={setSelectedTrunk}
                            trsRegistrations={trsRegistrations}
                            onRemoveFromPending={handleRemoveFromPending}
                            errors={errors}
                            touched={touched}
                            values={values}
                        />
                        {provided.placeholder}
                    </Box>
                )}
            </Droppable>
        );

        const staticFields = [
            <EditNumberAccordion
                key={editNumberData?.id}
                errors={errors}
                touched={touched}
                value={values}
                editNumberData={editNumberData}
                previousEditNumberData={previousEditNumberData}
                setEditNumberData={setEditNumberData}
                addresses={addresses}
                assignedPlans={assignedPlans}
                outboundCallerIDs={outboundCallerIDs}
                trsRegistrations={trsRegistrations}
                registrationData={registrationData}
                tableRowData={tableRowData}
            />,
        ];

        const draggableFields = (
            <DragDropContext
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}>
                <Box aria-label="draggable-content-wrapper">
                    {(() => {
                        const standalonePlatformCount = platformItems.filter(
                            (item) => item.type !== 'group',
                        ).length;
                        const canCreateGroup =
                            standalonePlatformCount >= 2 ||
                            (pendingGroupPlatforms.length >= 1 &&
                                standalonePlatformCount >= 1);
                        if (platformList?.length > 1) {
                            return (
                                <PlatformNumberWarning
                                    key="platformNumberWarning"
                                    isDragging={isDragging}
                                    isDraggingGroup={isDraggingGroup}
                                    dropZoneContent={dropZoneContent}
                                    hasPendingPlatforms={
                                        pendingGroupPlatforms.length > 0
                                    }
                                    canCreateGroup={canCreateGroup}
                                    simRingEnabled={simRingEnabled}
                                    failoverEnabled={failoverEnabled}
                                />
                            );
                        }
                        return null;
                    })()}

                    <Droppable
                        droppableId="unified-list"
                        type="PLATFORM"
                        renderClone={(provided, snapshot, rubric) =>
                            renderClone(provided, snapshot, {
                                ...rubric,
                                errors,
                            })
                        }>
                        {(provided, snapshot) => (
                            <Box
                                ref={provided.innerRef}
                                style={getListStyle(
                                    snapshot.isDraggingOver,
                                    darkMode,
                                )}
                                aria-label="draggable-unified-list-wrapper"
                                className="p-0"
                                {...provided.droppableProps}>
                                {platformItems
                                    .filter((item) => {
                                        // Keep all groups
                                        if (item.type === 'group') return true;
                                        // Filter out standalone platforms that are in pending
                                        return !pendingGroupPlatforms.some(
                                            (p) =>
                                                p.platformID ===
                                                item.platformID,
                                        );
                                    })
                                    .map((item, index) => {
                                        if (item.type === 'group') {
                                            return (
                                                <Draggable
                                                    key={item.id}
                                                    draggableId={item.id}
                                                    index={index}>
                                                    {(provided, snapshot) => (
                                                        <Box
                                                            ref={
                                                                provided.innerRef
                                                            }
                                                            {...provided.draggableProps}
                                                            style={getGroupStyle(
                                                                snapshot.isDragging,
                                                                provided
                                                                    .draggableProps
                                                                    .style,
                                                            )}
                                                            aria-label="draggable-group-item">
                                                            <GroupAccordion
                                                                group={item}
                                                                groupIndex={
                                                                    index
                                                                }
                                                                dragHandleProps={
                                                                    provided.dragHandleProps
                                                                }
                                                                onCloseGroup={
                                                                    handleCloseGroup
                                                                }
                                                                onRemovePlatform={(
                                                                    platformId,
                                                                ) =>
                                                                    handleRemovePlatformFromGroup(
                                                                        item.id,
                                                                        platformId,
                                                                    )
                                                                }
                                                                submitData={
                                                                    submitData
                                                                }
                                                                setSubmitData={
                                                                    setSubmitData
                                                                }
                                                                teamsTrunks={
                                                                    teamsTrunks
                                                                }
                                                                callingProfiles={
                                                                    callingProfiles
                                                                }
                                                                OC={OC}
                                                                selectedTrunk={
                                                                    selectedTrunk
                                                                }
                                                                setSelectedTrunk={
                                                                    setSelectedTrunk
                                                                }
                                                                previousData={
                                                                    previousData
                                                                }
                                                                trsRegistrations={
                                                                    trsRegistrations
                                                                }
                                                                errors={errors}
                                                                touched={
                                                                    touched
                                                                }
                                                                value={values}
                                                                isParentDragging={
                                                                    isDragging
                                                                }
                                                                onMoveUp={() =>
                                                                    handleMoveUp(
                                                                        item.id,
                                                                        true,
                                                                    )
                                                                }
                                                                onMoveDown={() =>
                                                                    handleMoveDown(
                                                                        item.id,
                                                                        true,
                                                                    )
                                                                }
                                                                isFirst={
                                                                    index === 0
                                                                }
                                                                isLast={
                                                                    index ===
                                                                    platformItems.filter(
                                                                        (i) =>
                                                                            i.type ===
                                                                                'group' ||
                                                                            !pendingGroupPlatforms.some(
                                                                                (
                                                                                    p,
                                                                                ) =>
                                                                                    p.platformID ===
                                                                                    i.platformID,
                                                                            ),
                                                                    ).length -
                                                                        1
                                                                }
                                                                totalItems={
                                                                    platformItems.filter(
                                                                        (i) =>
                                                                            i.type ===
                                                                                'group' ||
                                                                            !pendingGroupPlatforms.some(
                                                                                (
                                                                                    p,
                                                                                ) =>
                                                                                    p.platformID ===
                                                                                    i.platformID,
                                                                            ),
                                                                    ).length
                                                                }
                                                            />
                                                        </Box>
                                                    )}
                                                </Draggable>
                                            );
                                        } else {
                                            return (
                                                <Draggable
                                                    key={item.platformID}
                                                    draggableId={
                                                        item.platformID
                                                    }
                                                    index={index}>
                                                    {(provided, snapshot) => (
                                                        <Box
                                                            ref={
                                                                provided.innerRef
                                                            }
                                                            {...provided.draggableProps}
                                                            style={getItemStyle(
                                                                snapshot.isDragging,
                                                                provided
                                                                    .draggableProps
                                                                    .style,
                                                            )}>
                                                            <PlatformAccordion
                                                                platformId={
                                                                    item.platformID
                                                                }
                                                                platform={item}
                                                                errors={errors}
                                                                touched={
                                                                    touched
                                                                }
                                                                value={values}
                                                                submitData={
                                                                    submitData
                                                                }
                                                                setSubmitData={
                                                                    setSubmitData
                                                                }
                                                                teamsTrunks={
                                                                    teamsTrunks
                                                                }
                                                                callingProfiles={
                                                                    callingProfiles
                                                                }
                                                                OC={OC}
                                                                dragHandleProps={
                                                                    provided.dragHandleProps
                                                                }
                                                                platformIndex={
                                                                    index
                                                                }
                                                                platformNumbersLength={
                                                                    platformItems.filter(
                                                                        (i) =>
                                                                            i.type ===
                                                                                'group' ||
                                                                            !pendingGroupPlatforms.some(
                                                                                (
                                                                                    p,
                                                                                ) =>
                                                                                    p.platformID ===
                                                                                    i.platformID,
                                                                            ),
                                                                    ).length
                                                                }
                                                                selectedTrunk={
                                                                    selectedTrunk
                                                                }
                                                                setSelectedTrunk={
                                                                    setSelectedTrunk
                                                                }
                                                                previousData={
                                                                    previousData
                                                                }
                                                                trsRegistrations={
                                                                    trsRegistrations
                                                                }
                                                                isDragging={
                                                                    snapshot.isDragging
                                                                }
                                                                onMoveUp={() =>
                                                                    handleMoveUp(
                                                                        item.platformID,
                                                                        false,
                                                                    )
                                                                }
                                                                onMoveDown={() =>
                                                                    handleMoveDown(
                                                                        item.platformID,
                                                                        false,
                                                                    )
                                                                }
                                                                isFirst={
                                                                    index === 0
                                                                }
                                                                isLast={
                                                                    index ===
                                                                    platformItems.filter(
                                                                        (i) =>
                                                                            i.type ===
                                                                                'group' ||
                                                                            !pendingGroupPlatforms.some(
                                                                                (
                                                                                    p,
                                                                                ) =>
                                                                                    p.platformID ===
                                                                                    i.platformID,
                                                                            ),
                                                                    ).length -
                                                                        1
                                                                }
                                                            />
                                                        </Box>
                                                    )}
                                                </Draggable>
                                            );
                                        }
                                    })}
                                {provided.placeholder}
                            </Box>
                        )}
                    </Droppable>
                </Box>
            </DragDropContext>
        );

        return [...staticFields, draggableFields];
    };
    const countryFlag = editNumberData?.countries?.find(
        (country) => country?.id === editNumberData?.countryID,
    );
    const header = (
        <HStack>
            <Box sx={{ paddingBottom: '15px' }}>
                {editNumberData?.countryID !== null ? (
                    <>
                        <CountryFlag
                            countryCode={countryFlag?.alpha2.toLowerCase()}
                            title={countryFlag?.name}
                        />
                    </>
                ) : null}
            </Box>
            <Heading sx={headingStyling}>
                +{editNumberData?.telephoneNumber}
            </Heading>
        </HStack>
    );

    const accordionController = {
        openIndexes,
        setOpenIndexes,
    };

    const isSaveDisabled = (errors, dirty) => {
        if (dirty && Object.keys(errors).length === 0) return false;
        if (!isEqual(initialPlatformItems, platformItems)) return false;
        if (
            submitData === previousData &&
            editNumberData === previousEditNumberData
        )
            return true;
        if (errors && Object.keys(errors).length > 0) return true;

        if (!Array.isArray(submitData)) return false;

        const teamsOCPlatform = submitData.find(
            (platform) =>
                platform.platformID === PlatformEnums.TEAMS &&
                platform.trunkTypeID === TrunkType.OPERATOR_CONNECT,
        );

        return teamsOCPlatform && !teamsOCPlatform.isOCConfirmed;
    };
    return (
        <Drawer isOpen={isOpen} onClose={onClose}>
            {isFetchingData ? (
                <LoadingIndicator />
            ) : (
                <FormAccordionBase
                    header={header}
                    noBackBtn
                    initialValues={[
                        editNumberData,
                        ...(Array.isArray(submitData) ? submitData : []),
                    ]}
                    finalFormSubmit={finalFormSubmit}
                    controller={accordionController}
                    sideDrawer
                    extraCards={
                        // (2993) vendorService?.serviceType === 'VendorDefined' && hasActivatedServices
                        vendorService &&
                        vendorService?.serviceType === 'VendorDefined'
                            ? extraCards
                            : null
                    }
                    extraButton={
                        <Button
                            variant="outline"
                            sx={{
                                w: '142px',
                                mr: 'calc(100% - 150px)',
                            }}
                            onClick={onClose}>
                            Cancel
                        </Button>
                    }
                    ufBackBtn="platformEditDrawerBackBtn"
                    ufSaveBtn="platformEditDrawerSaveBtn"
                    // Pass a function that receives errors from FormAccordionBase
                    isSubmitDisabled={(errors, _, dirty) =>
                        isSaveDisabled(errors, dirty)
                    }>
                    {({ errors, touched, values }) =>
                        accordionFields(errors, touched, values)
                    }
                </FormAccordionBase>
            )}
        </Drawer>
    );
};

export default EditPlatformNumberDrawer;
